import { ID, ImageProcessingState, isUsable } from "@nova/constants"
import { InfraredImageState } from "plugins/InfraredImage/InfraredImageSlice"
import {
    selectOrthoOverlapLayerIsVisible,
    selectOrthoOverlapLayerVisibilityById,
    setOrthoOverlapBoundingBox,
    setOrthoOverlapLayer,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import store from "integrations/redux/store"
import { selectShowLayerByIdMapForHotspotOrthoOverlaps } from "features/map/layers/selectors"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { UploadState } from "@nova/slice-multifile-upload"
import { deactivateHotspotOrthoOverlapLayerAction } from "features/entity-caching/flights/slice"
import { MapLayerImage, MapLayerImages } from "features/map/layers/bitmap/types"
import { OrthoFlight } from "@nova/protobuf"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import { createOverlapDataForFlight } from "features/map/layers/bitmap/ortho-overlap/web-workers/drawFrameMask/createOverlapDataForFlight"
import { getVideoFrameBboxAsync } from "features/map/layers/video/VideoProjectionLayer"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { useAppSelector } from "integrations/redux/hooks"

export const useOrthoOverlap = ({
    allUploadsById,
    flights,
    type,
}: {
    allUploadsById: Record<string, UploadState<InfraredImageState>>
    flights: OrthoFlight[]
    type: "ir" | "rgb"
}) => {
    const irOrthoOverlapLayerVisibilityById = useAppSelector(
        selectShowLayerByIdMapForHotspotOrthoOverlaps
    )
    const rgbOrthoOverlapLayerVisibilityById = useAppSelector(
        selectOrthoOverlapLayerVisibilityById()
    )
    const overlapImages = useRef<MapLayerImages>({})
    const [statefulOverlapImages, setStatefulOverlapImages] = useState(overlapImages.current)
    const [overlapImagesVersion, setOverlapImagesVersion] = useState(0)

    const uploadCache = useRef<Record<ID, number>>({})

    const createOverlapData = useCallback(async () => {
        const state = store.getState()

        let needsRender = false

        for (const flight of flights) {
            const uploadsForFlight =
                type === "ir"
                    ? selectInfraredImagesAndUploadsByFlightId(flight.id)(state)
                    : selectOrthoImagesAndUploadsByFlightId(flight.id)(state)
            if (
                uploadsForFlight?.every((upload) => {
                    const usableUpload = isUsable(upload)
                    return (
                        usableUpload &&
                        ("state" in usableUpload
                            ? usableUpload.state >= ImageProcessingState.ProcessingDone
                            : true) &&
                        usableUpload.pitch &&
                        usableUpload.longitude &&
                        usableUpload.fov &&
                        usableUpload.height
                    )
                })
            ) {
                if (uploadCache.current[flight.id] === uploadsForFlight.length) continue
                uploadCache.current[flight.id] = uploadsForFlight.length
                if (uploadsForFlight.length === 0) continue

                const isVisible = (
                    type === "ir"
                        ? irOrthoOverlapLayerVisibilityById[flight.id]
                        : selectOrthoOverlapLayerIsVisible(flight.id)(state)
                ) as boolean | undefined

                const boxes: number[][][] = []
                for (const upload of uploadsForFlight as unknown as InfraredImageState[]) {
                    const box = await getVideoFrameBboxAsync([
                        upload.longitude!,
                        upload.latitude!,
                        upload.height!,
                        upload.pitch!,
                        upload.yaw!,
                        upload.roll!,
                        upload.fov!,
                        upload.altitude!,
                        !!upload.hasLRF,
                    ])
                    if (box) boxes.push(box)
                }
                const { imageData, width, height, bbox } = await createOverlapDataForFlight({
                    boxes,
                    // abortSignal: abortController.signal,
                })

                overlapImages.current[flight.id] = {
                    bbox,
                    imageData: new ImageData(imageData as ImageDataArray, width, height),
                    flightId: flight.id,
                    isVisible: !!isVisible, //isVisible !== false, TODO: use this one instead when we enable auto toggling
                }

                needsRender = true

                // Start at top left, going clockwise; lng, lat.
                const corners: [number, number][] = [
                    [bbox[0], bbox[1]],
                    [bbox[2], bbox[3]],
                ]

                store.dispatch(
                    setOrthoOverlapBoundingBox({
                        id: flight.id,
                        corners,
                    })
                )

                // Toggle on the ortho overlap layer if it hasn't been set yet and if the job hasn't been created.
                // Otherwise, set the visibility to false to create the button in the HotspotMenuContainer.
                if (isVisible === undefined) {
                    if (type === "ir") {
                        store.dispatch(
                            deactivateHotspotOrthoOverlapLayerAction({
                                id: flight.id,
                            })
                        )
                        // TODO: Replace the dispatch above with this one when the overlap math gets fixed.
                        // store.dispatch(
                        //     !flight.job
                        //         ? activateHotspotOrthoOverlapLayerAction({ id: flight.id })
                        //         : deactivateHotspotOrthoOverlapLayerAction({
                        //               id: flight.id,
                        //           })
                        // )
                    } else if (type === "rgb") {
                        store.dispatch(setOrthoOverlapLayer({ id: flight.id, active: false }))
                        // TODO: Replace the dispatch above with this one when the overlap math gets fixed.
                        // store.dispatch(setOrthoOverlapLayer({ id: flight.id, active: !flight.job }))
                    }
                }
            }
        }

        // Trigger a render since the overlapImages data has changed.
        if (needsRender) setOverlapImagesVersion((prev) => prev + 1)
    }, [overlapImages, flights, irOrthoOverlapLayerVisibilityById, type])

    useEffectUnsafe(() => {
        if (type !== "rgb") return

        for (const flightId of Object.keys(overlapImages.current)) {
            overlapImages.current[flightId].isVisible =
                rgbOrthoOverlapLayerVisibilityById[flightId] !== false
        }

        // Trigger a render since the overlapImages data has changed.
        setOverlapImagesVersion((prev) => prev + 1)
    }, [rgbOrthoOverlapLayerVisibilityById])

    useEffectUnsafe(() => {
        if (type !== "ir") return

        for (const flightId of Object.keys(overlapImages.current)) {
            overlapImages.current[flightId].isVisible =
                irOrthoOverlapLayerVisibilityById[flightId] !== false
        }

        // Trigger a render since the overlapImages data has changed.
        setOverlapImagesVersion((prev) => prev + 1)
    }, [irOrthoOverlapLayerVisibilityById])

    useEffect(() => {
        if (overlapImagesVersion > 0) {
            setStatefulOverlapImages({ ...overlapImages.current })
        }
    }, [overlapImagesVersion])

    useEffectUnsafe(() => {
        if (type === "ir") createOverlapData()
    }, [allUploadsById, irOrthoOverlapLayerVisibilityById, flights])

    useEffectUnsafe(() => {
        if (type === "rgb") createOverlapData()
    }, [allUploadsById, rgbOrthoOverlapLayerVisibilityById, flights])

    const visibleOverlapImages = useMemo(() => {
        const visibleOverlapImages: MapLayerImages = {}

        flights.forEach(({ id }) => {
            if (statefulOverlapImages[id]) visibleOverlapImages[id] = statefulOverlapImages[id]
        })

        return Object.values(visibleOverlapImages) as MapLayerImage[]
    }, [statefulOverlapImages, flights])

    return { overlapImages: visibleOverlapImages }
}
